import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal, MaskedLines, Eyebrow } from "@/components/Motion";
import { SOLUTIONS, PROCESS_STEPS, img } from "@/lib/content";

const CHAPTER_IMAGES = [
  "photo-1556761175-b413da4baf72",
  "photo-1497366754035-f200968a6e72",
  "photo-1560179707-f14e90ef3623",
  "photo-1554469384-e58fac16e23a",
];

const Employers = () => (
  <>
    <Seo
      title="For Employers — NEXREQ UAE & GCC"
      siteName="NEXREQ"
      description="Direct hire, executive search, RPO and talent advisory for organizations hiring across the UAE and GCC."
    />
    <PageHero
      eyebrow="For Employers"
      lines={["Hire With Precision.", "Build With Confidence."]}
      description="NEXREQ partners with ambitious organizations across the UAE and GCC to identify, attract and secure the people who move businesses forward."
      image={img("photo-1486406146926-c627a92ad1ab", 1920)}
    >
      <Reveal delay={0.75}>
        <div className="mt-10 flex flex-wrap gap-4">
          <Link to="/hire-talent" className="nx-btn-primary" data-testid="employers-hero-hire-btn">
            Submit a Hiring Requirement <ArrowRight size={15} />
          </Link>
          <Link to="/solutions" className="nx-btn-outline" data-testid="employers-hero-solutions-btn">
            Explore Solutions
          </Link>
        </div>
      </Reveal>
    </PageHero>

    <section className="bg-obsidian py-24 lg:py-32" data-testid="employers-intro">
      <div className="nx-container grid gap-14 lg:grid-cols-12 lg:items-center">
        <div className="lg:col-span-5">
          <Reveal>
            <Eyebrow>Why NEXREQ</Eyebrow>
          </Reveal>
          <h2 className="mt-6 font-heading text-3xl font-light leading-[1.08] tracking-tight text-white sm:text-4xl">
            <MaskedLines lines={["North American Standards.", "GCC Market Expertise."]} />
          </h2>
          <Reveal delay={0.2}>
            <p className="mt-6 leading-relaxed text-white/55">
              We bring the rigour of North American executive search — structured assessment, market
              mapping, transparent process — to the pace, relationships and nuance of Gulf hiring.
              Every mandate is led by a senior consultant and measured against outcomes, not
              activity.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.25} className="lg:col-span-7">
          <div className="overflow-hidden">
            <img
              src={img("photo-1497366216548-37526070297c", 1400)}
              alt="Premium office environment"
              className="aspect-[16/9] w-full object-cover grayscale-[25%]"
              loading="lazy"
            />
          </div>
        </Reveal>
      </div>
    </section>

    <section className="border-t border-white/[0.08] bg-obsidian py-24 lg:py-32" data-testid="employers-solutions">
      <div className="nx-container">
        <Reveal>
          <Eyebrow>Employer Solutions</Eyebrow>
        </Reveal>
        <div className="mt-14 space-y-24">
          {SOLUTIONS.map((s, i) => (
            <div
              key={s.name}
              className="grid gap-10 lg:grid-cols-12 lg:items-center"
              data-testid={`employer-solution-${i}`}
            >
              <Reveal className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="overflow-hidden">
                  <img
                    src={img(CHAPTER_IMAGES[i], 1400)}
                    alt={s.name}
                    className="aspect-[4/3] w-full object-cover grayscale-[30%] transition duration-700 hover:grayscale-0"
                    loading="lazy"
                  />
                </div>
              </Reveal>
              <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                <span className="font-mono text-xs tracking-[0.3em] text-emerald-500">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-4 font-heading text-2xl font-light tracking-tight text-white sm:text-3xl lg:text-4xl">
                  {s.name}
                </h3>
                <p className="mt-5 max-w-lg leading-relaxed text-white/55">{s.description}</p>
                <ul className="mt-7 space-y-3">
                  {s.deliverables.map((d) => (
                    <li key={d} className="flex items-start gap-3 text-sm text-white/70">
                      <Check size={15} className="mt-0.5 shrink-0 text-emerald-500" />
                      {d}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="border-t border-white/[0.08] bg-surface py-24 lg:py-32" data-testid="employers-process">
      <div className="nx-container">
        <Reveal>
          <Eyebrow>How We Work</Eyebrow>
        </Reveal>
        <h2 className="mt-6 max-w-2xl font-heading text-3xl font-light leading-[1.08] tracking-tight text-white sm:text-4xl">
          <MaskedLines lines={["A Disciplined Process.", "A Predictable Outcome."]} />
        </h2>
        <div className="mt-14 grid gap-px border border-white/[0.08] bg-white/[0.06] sm:grid-cols-2 lg:grid-cols-5">
          {PROCESS_STEPS.map((p, i) => (
            <div key={p.name} className="bg-surface p-7" data-testid={`process-step-${i}`}>
              <span className="font-mono text-[10px] text-emerald-500/80">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="mt-4 font-heading text-base font-medium text-white">{p.name}</h3>
              <p className="mt-3 text-[13px] leading-relaxed text-white/45">{p.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="border-t border-white/[0.08] bg-obsidian py-24" data-testid="employers-cta">
      <div className="nx-container grid gap-10 lg:grid-cols-[2fr_1fr] lg:items-end">
        <div>
          <h2 className="font-heading text-3xl font-light tracking-tight text-white sm:text-4xl lg:text-5xl">
            <MaskedLines lines={["Have a Role to Fill?"]} />
          </h2>
          <Reveal delay={0.15}>
            <p className="mt-5 max-w-xl leading-relaxed text-white/55">
              Tell us what you are hiring for. Our team will review your requirement and respond
              within one business day.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.25} className="lg:justify-self-end">
          <Link to="/hire-talent" className="nx-btn-primary" data-testid="employers-bottom-hire-btn">
            Submit a Hiring Requirement <ArrowRight size={15} />
          </Link>
        </Reveal>
      </div>
    </section>
  </>
);

export default Employers;
